import type { VoiceConsentRequestPayload, VoiceRightsPayload } from '@bunker/shared/socketEvents';
import i18n from '@/i18n';
import { toast } from '@/lib/toast';
import { voiceMedia } from '@/lib/voiceMedia';
import { getSocket } from '@/lib/socket';
import { canIn, PERMISSIONS } from '@/lib/guildPermissions';
import type { DataState } from '@/stores/dataStore';
import { askBroadcastConsent } from '@/components/BroadcastConsentModal';

/**
 * What the room I sit in still lets me send, after a role or override change.
 *
 * The server holds my microphone (no SPEAK) or camera and screen (no
 * STREAM_VIDEO) on its side and releases them when the right returns. The
 * microphone needs nothing from here but saying so — the mic button reads the
 * channel's permissions (`speakBlockedIn`). Camera and screen are stopped here:
 * a held video track would freeze on everyone's screen, and once the right is
 * back the person turns them on again themselves.
 */
/** Only the tab that holds the voice media acts: the others are not in the room. */
function holdsMedia(channelId: string): boolean {
  return voiceMedia.getSnapshot().activeChannelId === channelId;
}

export function applyVoiceRights(p: VoiceRightsPayload): void {
  if (!holdsMedia(p.channelId)) return;
  if (!p.video) {
    void voiceMedia.setVideoEnabled(false);
    void voiceMedia.setScreenShareEnabled(false);
    getSocket().emit('voice:state', { videoOn: false, screenOn: false });
  }
  if (!p.speak) toast.message(i18n.t('voice.rights.speakLost'));
  else if (!p.video) toast.message(i18n.t('voice.rights.videoLost'));
  else toast.message(i18n.t('voice.rights.restored'));
}

/** The room I sit in became a tournament room: ask me, without moving me. */
export function askConsentInRoom(p: VoiceConsentRequestPayload): void {
  if (!holdsMedia(p.channelId)) return;
  askBroadcastConsent(p.channelId, { inRoom: true });
}

/** Pure: does the room I sit in withhold SPEAK from me? (Not in voice: no.) */
export function speakBlockedIn(state: Pick<DataState, 'guilds' | 'myVoiceChannelId'>): boolean {
  const channelId = state.myVoiceChannelId;
  if (!channelId) return false;
  for (const guild of Object.values(state.guilds)) {
    const channel = guild.channels.find((c) => c.id === channelId);
    if (channel) return !canIn(channel, PERMISSIONS.SPEAK);
  }
  return false;
}
